import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Bell, Volume2, VolumeX, Monitor, Repeat, Pill } from "lucide-react";
import api from "@/lib/api";
import { fmtDateTime, fmtDate } from "@/lib/format";

const RefillItem = ({ m, onOpen }) => (
  <button data-testid="notification-item-refills" data-type="refills" onClick={onOpen}
    className="w-full text-left px-4 py-3 border-b border-slate-50 bg-violet-50/50 hover:bg-violet-50 transition-colors">
    <span className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><Pill className="h-3.5 w-3.5 text-violet-600" />Refills due</span>
    <div className="text-xs text-violet-700 mt-0.5">{m.due_soon} due in the next 3 days{m.overdue ? ` · ${m.overdue} overdue` : ""}{m.new ? ` · ${m.new} new` : ""}</div>
    {m.next && <div className="text-[11px] text-slate-500 mt-0.5">Next: {m.next.name} on {fmtDate(`${m.next.refill_date}T12:00:00`)}</div>}
  </button>
);

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 20000,
  });
  const markRead = useMutation({
    mutationFn: async () => api.post("/notifications/read"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const unread = data?.unread_count || 0;
  const items = data?.items || [];
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("notif_sound") !== "off");
  const [desktopOn, setDesktopOn] = useState(() => localStorage.getItem("notif_desktop") !== "off");
  const toggleSound = () => { const v = !soundOn; setSoundOn(v); localStorage.setItem("notif_sound", v ? "on" : "off"); };
  const toggleDesktop = () => {
    const v = !desktopOn; setDesktopOn(v); localStorage.setItem("notif_desktop", v ? "on" : "off");
    if (v && "Notification" in window && Notification.permission === "default") Notification.requestPermission().catch(() => {});
  };
  const prevUnread = useRef(null);

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (prevUnread.current !== null && unread > prevUnread.current) {
      if (soundOn) {
        try {
          const Ctx = window.AudioContext || window.webkitAudioContext;
          if (Ctx) {
            const ctx = new Ctx();
            const o = ctx.createOscillator();
            const g = ctx.createGain();
            o.connect(g); g.connect(ctx.destination);
            o.type = "sine"; o.frequency.value = 880; g.gain.value = 0.08;
            o.start(); o.stop(ctx.currentTime + 0.18);
          }
        } catch (e) { /* ignore */ }
      }
      if (desktopOn && "Notification" in window && Notification.permission === "granted") {
        const latest = items[0];
        const isLead = latest?.type === "returning_lead";
        const isRefill = latest?.type === "refills";
        new Notification(isRefill ? "Refills due" : isLead ? "Returning lead" : "New patient text", {
          body: !latest ? "You have a new notification" : isRefill
            ? `${latest.due_soon} due in the next 3 days${latest.overdue ? `, ${latest.overdue} overdue` : ""}` : isLead
            ? `${latest.lead_name} submitted again (×${latest.inquiry_count}) — follow up while interest is high`
            : `${latest.patient_name || latest.from_phone}: ${latest.body}`,
        });
      }
    }
    prevUnread.current = unread;
  }, [unread, items, soundOn, desktopOn]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) markRead.mutate();
  };

  return (
    <div className="relative">
      <button data-testid="notification-bell" onClick={toggle}
        className="relative h-9 w-9 rounded-lg border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50 transition-colors">
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span data-testid="notification-badge"
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-80 bg-white border border-slate-200 rounded-xl shadow-lg z-50 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <span className="font-heading font-bold text-slate-900 text-sm">Notifications</span>
              <div className="flex items-center gap-1.5">
                <button data-testid="toggle-sound" onClick={toggleSound} title={soundOn ? "Mute chime" : "Unmute chime"}
                  className={`h-7 w-7 rounded-md flex items-center justify-center transition-colors hover:bg-slate-100 ${soundOn ? "text-cyan-600" : "text-slate-300"}`}>
                  {soundOn ? <Volume2 className="h-3.5 w-3.5" /> : <VolumeX className="h-3.5 w-3.5" />}
                </button>
                <button data-testid="toggle-desktop" onClick={toggleDesktop} title={desktopOn ? "Disable desktop alerts" : "Enable desktop alerts"}
                  className={`h-7 w-7 rounded-md flex items-center justify-center transition-colors hover:bg-slate-100 ${desktopOn ? "text-cyan-600" : "text-slate-300"}`}>
                  <Monitor className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {items.length === 0 && (
                <div className="px-4 py-6 text-sm text-slate-400 text-center">No notifications yet.</div>
              )}
              {items.map((m, i) => m.type === "birthday_issues" ? (
                <button key="bday" data-testid="notification-item-birthday-issues" onClick={() => { setOpen(false); navigate("/birthdays"); }}
                  className="w-full text-left px-4 py-3 border-b border-slate-50 bg-red-50/50 hover:bg-red-50 transition-colors">
                  <span className="text-sm font-semibold text-slate-800">Birthday greetings need attention</span>
                  <div className="text-xs text-red-700 mt-0.5">{m.count} failed or uncertain this year — review in Birthdays › History</div>
                </button>
              ) : m.type === "refills" ? (
                <RefillItem key="refills" m={m} onOpen={() => { setOpen(false); navigate("/medications"); }} />
              ) : m.type === "returning_lead" ? (
                <button key={i} data-testid={`notification-item-${i}`} data-type="returning-lead"
                  onClick={() => { setOpen(false); navigate("/pipeline"); }}
                  className="w-full text-left px-4 py-3 border-b border-slate-50 bg-amber-50/40 hover:bg-amber-50 transition-colors">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-slate-800 truncate flex items-center gap-1.5">
                      <Repeat className="h-3.5 w-3.5 text-amber-600 shrink-0" />{m.lead_name}
                    </span>
                    <span className="text-[10px] text-slate-400 shrink-0">{fmtDateTime(m.created_at)}</span>
                  </div>
                  <div className="text-xs text-amber-700 mt-0.5">Returning lead ×{m.inquiry_count} · {m.service_interest || "General inquiry"} — follow up</div>
                </button>
              ) : (
                <button key={i} data-testid={`notification-item-${i}`}
                  onClick={() => { setOpen(false); navigate("/communication"); }}
                  className="w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-slate-800 truncate">{m.patient_name || m.from_phone}</span>
                    <span className="text-[10px] text-slate-400 shrink-0">{fmtDateTime(m.created_at)}</span>
                  </div>
                  <div className="text-xs text-slate-500 truncate mt-0.5">{m.body}</div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
